import { INavRoute, IUserPermissions } from 'types'
import { getBackendUrl } from 'utils/api/apiHelpers'
import { capitalize } from 'utils/capitalize'
import { getUserCookieValues } from 'utils/userAuth'
import { userGroupBooleans } from 'utils/userTypes'
import { IUserTypes } from 'constants/userTypes'

export const generateBaseUrl = (appName = '') => {
  if (__STAGE__ && !__STAGING_NEW__) return `#/${appName}`
  return `/${appName}`
}

export const generatePageTitle = (appName: string) => {
  if (!appName) return 'Transfix'
  const branchDeploy = __GIT_BRANCH__ && __GIT_BRANCH__ !== 'main'
  const humanReadableFormat = capitalize(appName.replace(/[\W]|[_]+/g, ' '))
  if (branchDeploy) return `${humanReadableFormat} | ${__GIT_BRANCH__} | Transfix`
  return `${humanReadableFormat} | Transfix`
}

interface IRouteFilterOptions {
  excludeBase: boolean
}

export const filterRoutes = (routes: INavRoute[], options?: IRouteFilterOptions): INavRoute[] => {
  const hashRoutes = __STAGE__ && !__STAGING_NEW__
  const { hash = '', pathname = '' } = global.location || {}
  const routeToSplit = hashRoutes ? hash : pathname
  const [, currentRoute, secondLevel] = routeToSplit.split('/')

  return routes.map(route => {
    const { childRoutes, location } = route

    /**
     * Prepend base paths when applicable
     */
    if (!options?.excludeBase && !route.excludeBase) {
      route.location = `${__ROOT__}/${route.location}`
    }

    /**
     * Uses `global` object to detect if current route is active
     * @deprecated by leveraging react-router
     */
    if (location && location?.indexOf(currentRoute) > -1) {
      if (secondLevel) {
        route.isActive = location?.indexOf(secondLevel) > -1
      } else {
        route.isActive = true
      }
    }

    if (childRoutes) {
      route.childRoutes = filterRoutes(childRoutes, options)
    }

    return route
  })
}

export const generateDefaultRoutes = (
  permissions: IUserPermissions | null,
  isFPUser = false,
  isTMSUser = false,
  isRogersShipmentCreateFormEnabled = false,
  isMpShippersPageEnabled = false,
  isRogersAuthPagesEnabled = false,
  isSpotQuoteTool = false
) => {
  const { authToken, groups, userType } = getUserCookieValues()

  if (!authToken || !permissions) {
    return filterRoutes([
      {
        location: 'login',
        name: 'Log In',
        isStark: false,
        display: true,
      },
    ])
  }

  const { isAdmin = false, isCarrier = false, isShipper = false } = userGroupBooleans(
    userType as keyof IUserTypes
  )
  const isCeRep = !!(groups && groups.includes('Carrier Experience'))
  const isCxRep = !!(groups && groups.includes('Customer Experience'))
  const isCmRep = groups && (groups.includes('CM') || groups.includes('Cm'))
  const isNorthEast = groups && groups.includes('NorthEast')
  const isNorthEastCmRep = !!(isCmRep && isNorthEast)

  const navFacilities = isAdmin || isShipper

  const newShipmentLink = isRogersShipmentCreateFormEnabled ? 'create-shipment' : 'shipments/new'

  const routes = filterRoutes([
    {
      location: isTMSUser ? 'tms/home' : 'dashboard',
      name: 'Dashboard',
      isStark: false,
      display: isShipper,
    },
    {
      location: isFPUser ? 'fms/home' : 'home',
      name: 'Home',
      display: isCarrier,
    },
    {
      location: isFPUser ? 'fms/book' : 'shipments/bookable',
      name: 'Book Loads',
      isStark: !isFPUser,
      display: isCarrier,
    },
    {
      location: isFPUser ? 'fms/loads' : 'shipments',
      name: isCarrier ? 'My Shipments' : 'Shipments',
      isStark: !isFPUser,
      display: !isAdmin,
    },
    {
      location: 'task-assignments',
      name: 'Tasks',
      display: isAdmin || isCeRep || isNorthEastCmRep || isCxRep,
    },
    {
      location: 'booking',
      name: 'Shipments',
      display: isAdmin,
    },
    {
      location: 'carriers',
      name: 'Carriers',
      display: isAdmin,
      childRoutes: [
        {
          location: 'lane',
          name: 'Manage Lanes',
          display: isAdmin,
        },
        {
          location: 'lane/assignment',
          name: 'Lane Assignments',
          display: isAdmin,
        },
        {
          location: 'awarded-lanes',
          name: 'Awarded Lanes',
          display: isAdmin,
        },
        {
          location: 'rfp-lanes',
          name: 'Sourced Rates',
          display: isAdmin,
        },
        {
          location: 'staples-headhaul-lanes',
          name: 'Staples Headhaul Lanes',
          display: isAdmin,
        },
      ],
    },
    {
      location: 'shippers',
      name: 'Shippers',
      isStark: !isMpShippersPageEnabled,
      display: isAdmin,
      childRoutes: [
        {
          location: 'spot-control-panel',
          name: 'Spot Control Panel',
          display: isAdmin,
        },
        {
          location: isSpotQuoteTool ? 'spot-quote-tool' : 'am-quoting-tool',
          name: isSpotQuoteTool ? 'Spot Quote Tool' : 'AM Quoting Tool',
          display: isAdmin,
        },
        {
          location: 'instant-rfp',
          name: 'Instant RFP',
          display: isAdmin,
        },
        {
          location: 'shipper-rates',
          name: 'Shipper Rates',
          display: isAdmin,
        },
        {
          location: 'shipper-rate-matcher',
          name: 'Shipper Rate Matcher',
          display: isAdmin,
        },
      ],
    },
    {
      location: isFPUser ? 'fms/team' : 'users',
      name: 'Users',
      display: isAdmin,
    },
    {
      location: '',
      name: 'More',
      display: true,
      childRoutes: [
        {
          location: 'facilities',
          name: 'Facilities',
          isStark: false,
          display: navFacilities,
        },
        {
          location: 'groups',
          name: 'Groups',
          display: isAdmin,
        },
        {
          location: `${getBackendUrl('MARKETPLACE')}/authorization_admin`,
          name: 'Permissions',
          isStark: false,
          excludeBase: true,
          display: isAdmin,
        },
        {
          location: 'accounting-tools',
          name: 'Accounting Tool',
          isStark: false,
          display: isAdmin,
        },
        {
          location: 'issues/control-panel',
          name: 'Issue Control Panel',
          isStark: false,
          display: isAdmin,
        },
        {
          location: isRogersAuthPagesEnabled ? 'signout' : 'user/signout',
          name: 'Sign Out',
          isStark: !isRogersAuthPagesEnabled,
          display: true,
        },
      ],
    },
    {
      location: isSpotQuoteTool ? 'spot-quote-tool' : 'am-quoting-tool',
      name: 'Quote',
      isButton: true,
      display: isAdmin,
    },
    {
      location: isTMSUser ? 'tms/shipments/new' : newShipmentLink,
      name: 'Add Shipment',
      isStark: !isRogersShipmentCreateFormEnabled,
      isButton: true,
      isPrimary: true,
      display: isShipper || isAdmin,
    },
  ])

  return routes
}
